import type {MaterialStorage} from '@domain/material-storage'
import {dataTestId} from '@hconnect/uikit'
import {MoreVert, DownloadDone, DoDisturb} from '@mui/icons-material'
import {LoadingButton} from '@mui/lab'
import {Stack, Divider, Menu, MenuItem} from '@mui/material'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useHandleDeleteStorageFromPlanning, useHandleEnableStorageForPlanning} from './hooks'

import {usePermission} from '@settings/modules/permissions'

interface StoragePlanningMenu {
  storage?: MaterialStorage
}

export const StoragePlanningMenu: React.FC<StoragePlanningMenu> = ({storage}) => {
  const {t} = useTranslation()
  const canChangeStorages = usePermission('CHANGE_MATERIAL_STORAGE')

  const {isAddingStorage, handleEnableStorageForPlanning} = useHandleEnableStorageForPlanning()
  const {isDeletingStorage, handleDeleteStorageFromPlanning} =
    useHandleDeleteStorageFromPlanning(storage)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = !!anchorEl

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const isPlanningMenuDisabled = !canChangeStorages
  const isEnableForPlanningDisabled = !!storage
  const isDeleteFromPlanningDisabled = !storage

  return (
    <>
      <LoadingButton
        loading={isAddingStorage || isDeletingStorage}
        disabled={isPlanningMenuDisabled}
        onClick={handleMenuOpen}
        sx={({spacing, palette}) => ({
          minWidth: spacing(5.5),
          minHeight: spacing(5.5),
          padding: 0,
          backgroundColor: isMenuOpen ? palette.action.hover : 'none'
        })}
        {...dataTestId('plant-structure-details-planning-menu-button')}
      >
        <MoreVert fontSize="small" />
      </LoadingButton>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{marginTop: 1}}
        {...dataTestId('plant-structure-details-planning-menu')}
      >
        <MenuItem
          disabled={isEnableForPlanningDisabled}
          onClick={() => {
            handleEnableStorageForPlanning()
            handleMenuClose()
          }}
          sx={({palette}) => ({
            color: palette.primary.main,
            '&.Mui-disabled .MuiSvgIcon-root': {
              color: palette.primary.main
            }
          })}
          {...dataTestId('plant-structure-details-enable-for-planning-button')}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <DownloadDone fontSize="inherit" />
            <span>{t('plantSetup.enableForPlanningStorage')}</span>
          </Stack>
        </MenuItem>
        <Divider variant="middle" />
        <MenuItem
          disabled={isDeleteFromPlanningDisabled}
          onClick={() => {
            handleDeleteStorageFromPlanning()
            handleMenuClose()
          }}
          sx={({palette}) => ({
            color: palette.error.main,
            '&.Mui-disabled .MuiSvgIcon-root': {
              color: palette.error.main
            }
          })}
          {...dataTestId('plant-structure-details-delete-from-planning-button')}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <DoDisturb fontSize="inherit" />
            <span>{t('plantSetup.deleteFromPlanning')}</span>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  )
}
